import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@nfp-tracker/components";
import {
  BLEEDING_OPTIONS,
  MUCUS_COLOR_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS,
  MUCUS_CONSISTENCY_OPTIONS_BASIC,
  usesMonitor,
} from "@nfp-tracker/utils";
import { HelpCircle } from "lucide-react";
import HealthDisclaimer from "./HealthDisclaimer";

interface TrackingGuideProps {
  isPostpartum?: boolean;
  methods?: string[];
  /** Shown when the couple has enabled the optional ovulation-test (OPK) field. */
  showOvulationTest?: boolean;
}

// Bleeding intensity descriptions, keyed by BLEEDING_OPTIONS' label so this list can never
// drift out of sync with the actual selectable values in Observations.tsx.
const BLEEDING_DESCRIPTIONS: Record<string, string> = {
  "Very Light": "Minimal spotting",
  Light: "Spotting, light flow requiring minimal protection",
  Medium: "Regular flow requiring standard protection",
  Heavy: "Heavy flow requiring frequent changes",
  Brown: "Old/discolored blood — often lighter spotting, common at the start or end of a period",
};

const TrackingGuide = ({
  isPostpartum = false,
  methods = [],
  showOvulationTest = false,
}: TrackingGuideProps) => {
  const isCreighton = methods.includes("Creighton");
  const isBillings = methods.includes("Billings");
  const isMarquette = methods.includes("Marquette");
  const isSymptoThermal = methods.includes("Sympto-Thermal");
  // Creighton and Billings get the detailed consistency+color vocabulary; everyone else
  // (Marquette, Sympto-Thermal, Other) uses the simpler 5-value picker — matches exactly
  // how Observations.tsx branches its mucus section.
  const isExpandedMucusMethod = isCreighton || isBillings;
  const methodLabel = methods.length > 0 ? methods.join(" & ") : "your method";
  const expandedMucusLabel =
    methods.filter((m) => m === "Creighton" || m === "Billings").join(" & ") || "This method";
  const guideTitle =
    methods.length === 0
      ? "NFP Tracking Guide"
      : methods.length === 1
      ? `${methods[0]} Guide`
      : `${methodLabel} Guide`;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <HelpCircle className="h-4 w-4" />
          Tracking Guide
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{guideTitle}</DialogTitle>
          <DialogDescription>
            Reference information for logging observations and reviewing common method terminology.
          </DialogDescription>
        </DialogHeader>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="bleeding">
            <AccordionTrigger>Bleeding Intensity</AccordionTrigger>
            <AccordionContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Record your bleeding using these categories:
              </p>
              <ul className="text-sm space-y-2 ml-4">
                {BLEEDING_OPTIONS.map((option) => (
                  <li key={option.value}>
                    <strong>{option.label}:</strong> {BLEEDING_DESCRIPTIONS[option.label]}
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>

          {/* Shown when any selected method uses a monitor, matching Observations.tsx's Monitor section. */}
          {usesMonitor(methods) && (
            <AccordionItem value="monitor">
              <AccordionTrigger>Monitor Readings</AccordionTrigger>
              <AccordionContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Record the reading displayed on your fertility monitor exactly as shown:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li><strong>Low:</strong> A monitor state reported by the device</li>
                  <li><strong>High:</strong> A monitor state reported by the device</li>
                  <li><strong>Peak:</strong> A monitor state reported by the device</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>Important:</strong> Use the monitor manufacturer&apos;s instructions and your instructor&apos;s guidance to interpret what those readings mean for you.
                </p>
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="mucus">
            <AccordionTrigger>Mucus Observations</AccordionTrigger>
            <AccordionContent className="space-y-2">
              {isExpandedMucusMethod ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {expandedMucusLabel} tracks mucus as two separate observations — consistency and color/appearance:
                  </p>
                  <p className="text-sm font-medium mt-2">Consistency</p>
                  <ul className="text-sm space-y-1 ml-4">
                    {MUCUS_CONSISTENCY_OPTIONS.map((option) => (
                      <li key={option.value}>
                        <strong>{option.label}</strong>
                        {option.peak && " — Peak-type quality"}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm font-medium mt-2">Color / Appearance (optional)</p>
                  <ul className="text-sm space-y-1 ml-4">
                    {MUCUS_COLOR_OPTIONS.map((option) => (
                      <li key={option.value}>{option.label}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground mt-3">
                    <strong>Focus:</strong> Log the most significant mucus observation of the day.
                    A &quot;Lubricative&quot; color/appearance is treated as a Peak-type signal
                    regardless of consistency.
                  </p>
                </>
               ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Cervical mucus changes throughout your cycle:
                  </p>
                   <ul className="text-sm space-y-2 ml-4">
                     {MUCUS_CONSISTENCY_OPTIONS_BASIC.map((option) => (
                       <li key={option.value}>
                         <strong>{option.label}</strong>
                         {option.peak && " — indicates approaching fertility"}
                       </li>
                     ))}
                   </ul>
                   <p className="text-sm text-muted-foreground mt-3">
                      {isMarquette
                        ? "For Marquette: mucus tracking is optional and can be logged alongside monitor readings."
                        : "Log the observation that best matches what you noticed that day."
                      }
                   </p>
                 </>
              )}
            </AccordionContent>
          </AccordionItem>

          {showOvulationTest && (
            <AccordionItem value="ovulation-test">
              <AccordionTrigger>Ovulation tests (OPK)</AccordionTrigger>
              <AccordionContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Ovulation predictor kits (LH test strips) detect the surge of luteinizing hormone
                  that precedes ovulation:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li><strong>Positive:</strong> An LH surge was detected. This is not ovulation itself — ovulation usually follows within about a day</li>
                  <li><strong>Negative:</strong> No surge detected on that test</li>
                  <li><strong>Not tested:</strong> Leave the field blank on days you did not test</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3">
                  <strong>Important:</strong> A positive test corroborates your chart but does not
                  replace your method&apos;s Peak Day rule. Keep identifying Peak Day and the fertile
                  window from your method&apos;s own observations and your instructor&apos;s guidance.
                </p>
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="symptoms">
            <AccordionTrigger>Common Symptoms</AccordionTrigger>
              <AccordionContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Track any symptoms you experience so you have a complete record to review later:
                </p>
                <ul className="text-sm space-y-1 ml-4">
                  <li>• Cramps</li>
                  <li>• Headache</li>
                  <li>• Mood Changes</li>
                  <li>• Fatigue</li>
                  <li>• Bloating</li>
                  <li>• Breast Tenderness</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

          {isPostpartum && (
            <AccordionItem value="postpartum">
              <AccordionTrigger>Postpartum Tracking</AccordionTrigger>
              <AccordionContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Postpartum charts can change quickly and are often harder to interpret:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li><strong>Log consistently:</strong> Daily entries make your chart easier to review with a professional</li>
                  <li><strong>Expect variation:</strong> Early postpartum patterns may not match your usual cycle</li>
                  <li><strong>Review changes:</strong> Discuss postpartum chart interpretation with your clinician or instructor</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md">
                  <strong>Note:</strong> If you are postpartum and need help interpreting your chart, use the official method resources and professional guidance linked below.
                </p>
              </AccordionContent>
            </AccordionItem>
          )}

          {isMarquette && (
            <AccordionItem value="marquette">
              <AccordionTrigger>Marquette Method Notes</AccordionTrigger>
              <AccordionContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  This app can record the types of entries commonly used in Marquette charting:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li><strong>Monitor entries:</strong> Low, High, and Peak readings can be logged exactly as shown on the device</li>
                  <li><strong>Sequence tracking:</strong> The app can summarize post-peak progression from your recorded entries</li>
                  <li><strong>Interpretation:</strong> Follow official Marquette materials and your instructor for method use</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}

          {isExpandedMucusMethod && (
            <AccordionItem value="creighton">
              <AccordionTrigger>
                {isCreighton && isBillings
                  ? "Creighton & Billings Method Notes"
                  : isBillings
                  ? "Billings Method Notes"
                  : "Creighton Model Notes"}
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {expandedMucusLabel} focuses on detailed mucus observations and sensation notes:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li><strong>Daily entries:</strong> Record the most significant mucus observation and sensation you noticed that day</li>
                  <li><strong>Peak tracking:</strong> The app can store the peak-related entries you log</li>
                  <li>
                    <strong>Interpretation:</strong> Use official {expandedMucusLabel} materials
                    and practitioner guidance for method rules
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        <div className="mt-6">
          <HealthDisclaimer compact showSources />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrackingGuide;
